/**
 * 汎用メッセージ・タスク表示UIコンポーネント
 *
 * メッセージとタスクの両方で使用できる共通のUIコンポーネントです。
 * リスト表示では30文字までに制限し、クリックすると全文を表示します。
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { memo, type ReactElement, useCallback, useState } from 'react';

/**
 * 汎用メッセージアイテムのProps型定義
 */
type GenericMessageItemProps = {
  id: string;
  title: string;
  content: string;
  borderColor: string;
  isRead?: boolean;
  statusText?: string;
  statusColor?: string;
  onItemClick?: (id: string) => void;
};

/**
 * テキストを30文字で切り詰める
 */
function truncateText(text: string, maxLength: number = 30): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}…`;
}

/**
 * 汎用メッセージアイテムコンポーネント
 *
 * @param {GenericMessageItemProps} props - コンポーネントのプロパティ
 * @returns {ReactElement} 汎用メッセージアイテムコンポーネント
 */
export const GenericMessageItem = memo(
  ({
    id,
    title,
    content,
    borderColor,
    isRead = true,
    statusText,
    statusColor,
    onItemClick,
  }: GenericMessageItemProps): ReactElement => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const theme = useTheme();
    const statusAccent = statusColor ?? theme.palette.text.secondary;

    /**
     * アイテムクリックハンドラー
     */
    const handleItemClick = useCallback(() => {
      setIsDialogOpen(true);
      if (onItemClick) {
        onItemClick(id);
      }
    }, [id, onItemClick]);

    /**
     * ダイアログを閉じる
     */
    const handleCloseDialog = useCallback(() => {
      setIsDialogOpen(false);
    }, []);

    const truncatedTitle = truncateText(title, 30);
    const truncatedContent = truncateText(content, 30);

    return (
      <>
        <ListItem
          disablePadding
          sx={{
            borderRadius: 1.5,
            border: `1px solid ${alpha(borderColor, 0.25)}`,
            backgroundColor: alpha(borderColor, isRead ? 0.04 : 0.08),
            boxShadow: 'none',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            '&:not(:last-of-type)': {
              marginBottom: '10px',
            },
            '&:hover': {
              borderColor: alpha(borderColor, 0.45),
            },
          }}
        >
          <ListItemButton
            onClick={handleItemClick}
            sx={{
              alignItems: 'flex-start',
              gap: 0.75,
              py: 1,
              px: 1.2,
              borderRadius: 1.5,
            }}
          >
            <ListItemText
              primary={truncatedTitle}
              secondary={
                <>
                  {truncatedContent}
                  {statusText && (
                    <>
                      <br />
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: statusAccent,
                          fontWeight: 600,
                          display: 'inline-block',
                          mt: 0.5,
                          letterSpacing: 0.2,
                        }}
                      >
                        {statusText}
                      </Typography>
                    </>
                  )}
                </>
              }
              primaryTypographyProps={{
                sx: {
                  fontSize: '0.9rem',
                  fontWeight: isRead ? 500 : 600,
                  color: alpha(theme.palette.text.primary, isRead ? 0.8 : 0.9),
                },
              }}
              secondaryTypographyProps={{
                sx: {
                  fontSize: '0.75rem',
                  color: alpha(theme.palette.text.secondary, 0.85),
                },
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* 詳細表示ダイアログ */}
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '14px',
              border: `1px solid ${alpha(borderColor, 0.15)}`,
              boxShadow: `0 18px 40px ${alpha(borderColor, 0.2)}`,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '1.05rem',
              fontWeight: 600,
              borderBottom: `1px solid ${alpha(borderColor, 0.15)}`,
              pb: 1.25,
              backgroundColor: alpha(borderColor, 0.08),
            }}
          >
            {title}
          </DialogTitle>
          <DialogContent
            sx={{
              pt: 2.5,
              pb: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.95rem',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {content}
            </Typography>
            {statusText && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  color: statusAccent,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                ステータス: {statusText}
              </Typography>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              borderTop: `1px solid ${alpha(borderColor, 0.1)}`,
            }}
          >
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

GenericMessageItem.displayName = 'GenericMessageItem';
